/**
 * Peças de UI da fila do comprador (ADR-0069): avatar de iniciais, barra
 * segmentada de itens, tag de origem, pill de status com dot, controle
 * segmentado, modal de motivo. Tudo em tokens `var(--*)`.
 */
import React, { useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiHelpCircle, FiPaperclip } from "react-icons/fi";
import styled from "styled-components";

import { MiniSpinner } from "../../../components/LoadingMarcenaria";
import { ORIGEM_LABEL, ORIGEM_TONE } from "../../../services/compras";
import { COR_SEGMENTO_ITEM, SITUACAO_LABEL, SITUACAO_TONE, rotuloPrazo } from "../../../services/requisicoesCompraUi";
import { getToneTokens } from "../../../styles/shared";
import { formatarDataLocal } from "../../../utils/dinheiro";
import { Pill, Tag } from "../_shared/quadro/styles";

// ---- Avatar de iniciais --------------------------------------------------

const PALETA_AVATAR = ["accent", "info", "success", "violet", "teal", "warning"];

function corDoNome(nome) {
  if (!nome) return null;
  let h = 0;
  for (let i = 0; i < nome.length; i += 1) h = (h * 31 + nome.charCodeAt(i)) | 0; // eslint-disable-line no-bitwise
  return PALETA_AVATAR[Math.abs(h) % PALETA_AVATAR.length];
}

export function iniciais(nome) {
  const partes = String(nome || "").trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return "—";
  return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : "")).toUpperCase();
}

const AvatarWrap = styled.span`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $size }) => Math.round($size * 0.4)}px;
  font-weight: 600;
  flex: 0 0 auto;
  ${({ $tone }) => {
    if (!$tone) return "background: var(--surface-3); color: var(--ink-4);";
    if ($tone === "accent") return "background: var(--accent-soft); color: var(--accent);";
    const t = getToneTokens(null, $tone);
    return `background: ${t.background}; color: ${t.text};`;
  }}
`;

export function Avatar({ nome, size = 28, title }) {
  return (
    <AvatarWrap $size={size} $tone={corDoNome(nome)} title={title || nome || "Sem responsável"} aria-label={nome || "Sem responsável"}>
      {iniciais(nome)}
    </AvatarWrap>
  );
}

// ---- Barra segmentada de itens ------------------------------------------

const Barra = styled.div`
  display: flex;
  gap: 2px;
  height: ${({ $h }) => $h || 5}px;
  width: 100%;
  min-width: 0;
  > span { flex: 1; border-radius: 2px; }
`;

export function BarraItens({ situacoes = [], altura }) {
  if (!situacoes.length) return null;
  return (
    <Barra $h={altura} aria-hidden="true">
      {situacoes.map((s, i) => (
        <span key={i} data-testid="segmento-item" data-situacao={s} style={{ background: COR_SEGMENTO_ITEM[s] || "var(--border-strong)" }} />
      ))}
    </Barra>
  );
}

/** "2/4 comprados" + badge "1 s/ anexo" + barra. */
export function ProgressoItens({ req, compacto = false }) {
  const total = req.itens_total ?? req.qtd_itens ?? 0;
  const comprados = req.itens_comprados ?? 0;
  const semAnexo = req.itens_sem_anexo ?? 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
      {!compacto ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, minWidth: 0 }}>
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            <strong>{comprados}</strong>/{total} comprados
          </span>
          {semAnexo > 0 ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, color: "var(--warning-text)", fontWeight: 600, flexShrink: 0 }} title="Itens de terceiro sem detalhamento anexado">
              <FiPaperclip size={11} /> {semAnexo} s/ anexo
            </span>
          ) : null}
        </div>
      ) : null}
      <BarraItens situacoes={req.itens_situacoes || []} altura={compacto ? 4 : 5} />
    </div>
  );
}

// ---- Pills e tags --------------------------------------------------------

export function StatusPill({ situacao, curto = false, ...rest }) {
  return (
    <Pill $tone={SITUACAO_TONE[situacao] || "neutral"} $dot {...rest}>
      {SITUACAO_LABEL[situacao] || situacao}
    </Pill>
  );
}

const ORIGEM_CURTA = {
  FERRAGEM_FALTA: "Ferragem", TERCEIROS: "Terceiros", MANUAL: "Manual", LOTE_MDF: "Lote", PHARUCUT: "PharuCut", RQC: "RQC",
};

export function TagOrigem({ origem, label, curta = false, ...rest }) {
  const texto = curta ? (ORIGEM_CURTA[origem] || label || origem) : (ORIGEM_LABEL[origem] || label || origem);
  return <Tag $tone={ORIGEM_TONE[origem] || "neutral"} $h={curta ? 20 : 24} title={ORIGEM_LABEL[origem] || label} {...rest}>{texto}</Tag>;
}

export function TagUrgente() {
  return <Tag $tone="danger"><FiAlertTriangle size={11} /> Urgente</Tag>;
}

/** Data do prazo + rótulo relativo (“atrasada 3d”, “vence hoje”, “em 4d”). */
export function PrazoCell({ req, alinhar = "left" }) {
  const info = rotuloPrazo(req.dias_para_prazo);
  const atrasada = req.atrasada;
  const cor = atrasada ? "var(--danger-text)" : info.tone === "warning" ? "var(--warning-text)" : "var(--ink-4)";
  return (
    <div style={{ minWidth: 0, textAlign: alinhar }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: atrasada ? "var(--danger-text)" : "var(--ink)" }}>
        {req.prazo_cotacao ? formatarDataLocal(req.prazo_cotacao) : "—"}
      </div>
      <div style={{ fontSize: 11, fontWeight: info.tone === "neutral" ? 400 : 600, color: cor }}>
        {info.texto}
      </div>
    </div>
  );
}

// ---- Controle segmentado -------------------------------------------------

const SegWrap = styled.div`
  display: inline-flex;
  padding: 3px;
  gap: 3px;
  border-radius: 9px;
  background: var(--surface-2);
  max-width: 100%;
  flex-wrap: wrap;
`;

const SegBtn = styled.button`
  height: 28px;
  padding: 0 12px;
  border-radius: 7px;
  border: none;
  font: inherit;
  font-size: 12px;
  font-weight: ${({ $on }) => ($on ? 600 : 500)};
  cursor: pointer;
  white-space: nowrap;
  transition: background .15s ease, color .15s ease;
  ${({ $on, $tone }) => {
    if (!$on) return "background: transparent; color: var(--ink-4);";
    if (!$tone || $tone === "accent") return "background: var(--surface); color: var(--ink); box-shadow: var(--shadow-1);";
    const t = getToneTokens(null, $tone);
    return `background: ${t.background}; color: ${t.text};`;
  }}
  &:hover:not(:disabled) { color: var(--ink); }
  &:disabled { cursor: not-allowed; opacity: .6; }
`;

/** `opcoes`: [{ value, label, tone? }]. Largura pelo conteúdo, nunca fixa. */
export function Segmented({ opcoes, valor, onChange, disabled = false, ariaLabel }) {
  return (
    <SegWrap role="radiogroup" aria-label={ariaLabel}>
      {opcoes.map((o) => (
        <SegBtn
          key={o.value}
          type="button"
          role="radio"
          aria-checked={valor === o.value}
          $on={valor === o.value}
          $tone={o.tone}
          disabled={disabled}
          onClick={() => valor !== o.value && onChange?.(o.value)}
        >
          {o.label}
        </SegBtn>
      ))}
    </SegWrap>
  );
}

// ---- Toggle --------------------------------------------------------------

const ToggleBtn = styled.button`
  width: 30px;
  height: 18px;
  border-radius: 999px;
  border: none;
  padding: 0;
  position: relative;
  cursor: pointer;
  flex: 0 0 auto;
  background: ${({ $on }) => ($on ? "var(--accent)" : "var(--border-strong)")};
  transition: background .15s ease;
  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${({ $on }) => ($on ? 14 : 2)}px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    transition: left .15s ease;
  }
`;

export function ToggleLinha({ titulo, descricao, on, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 13, fontWeight: 600 }}>{titulo}</span>
        {descricao ? <span style={{ display: "block", fontSize: 12, color: "var(--ink-3)" }}>{descricao}</span> : null}
      </span>
      <ToggleBtn type="button" role="switch" aria-checked={on} $on={on} onClick={() => onChange(!on)} />
    </label>
  );
}

// ---- Rótulo de seção / campo --------------------------------------------

export const Rotulo = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--ink-3);
  margin-bottom: 6px;
`;

export const Eyebrow = styled.div`
  font-family: var(--font-mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: var(--ink-3);
`;

// ---- Modal de motivo / observação ---------------------------------------

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 7, 12, .45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 16px;
`;

const Caixa = styled.div`
  width: min(100%, 460px);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow-4);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  h3 { margin: 0; font-size: 16px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
  p { margin: 0; font-size: 13px; color: var(--ink-3); }
  textarea {
    width: 100%;
    min-height: 88px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: 13px;
    resize: vertical;
    box-sizing: border-box;
  }
  textarea:focus { outline: none; border-color: var(--accent); box-shadow: var(--shadow-focus); }
`;

/**
 * Pede um texto (motivo de cancelar/reabrir, observação do recebimento).
 * `obrigatorio` bloqueia confirmar vazio. Substitui o `window.prompt`.
 */
export function ModalMotivo({
  open, titulo, descricao, rotulo = "Motivo", placeholder, obrigatorio = true,
  confirmarLabel = "Confirmar", tone = "primary", onConfirmar, onCancelar,
}) {
  const [texto, setTexto] = useState("");
  const [executando, setExecutando] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setTexto("");
    setExecutando(false);
    setTimeout(() => ref.current?.focus(), 30);
    const aoTeclar = (e) => { if (e.key === "Escape" && !executando) onCancelar?.(); };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;
  const podeConfirmar = !obrigatorio || texto.trim().length > 0;

  async function confirmar() {
    if (!podeConfirmar) return;
    setExecutando(true);
    try {
      await onConfirmar?.(texto.trim());
    } finally {
      setExecutando(false);
    }
  }

  const Icone = tone === "danger" ? FiAlertTriangle : FiHelpCircle;
  return (
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget && !executando) onCancelar?.(); }}>
      <Caixa role="dialog" aria-modal="true" aria-labelledby="modal-motivo-titulo">
        <h3 id="modal-motivo-titulo">
          <Icone size={18} style={{ color: tone === "danger" ? "var(--danger-solid)" : "var(--accent)" }} />
          {titulo}
        </h3>
        {descricao ? <p>{descricao}</p> : null}
        <div>
          <Rotulo>{rotulo}{obrigatorio ? "" : " (opcional)"}</Rotulo>
          <textarea
            ref={ref}
            value={texto}
            placeholder={placeholder}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") confirmar(); }}
            maxLength={300}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn--secondary" onClick={onCancelar} disabled={executando}>Voltar</button>
          <button
            type="button"
            className={`btn ${tone === "danger" ? "btn--danger" : "btn--primary"}`}
            onClick={confirmar}
            disabled={!podeConfirmar || executando}
          >
            {executando ? <MiniSpinner size={13} label="Aguarde" /> : null}
            {confirmarLabel}
          </button>
        </div>
      </Caixa>
    </Overlay>
  );
}

// Moldura do modal, reusada pela confirmacao da juncao (JuntarModal).
export { Overlay as ModalOverlay, Caixa as ModalCaixa };
